/* istanbul ignore file */
/**
 * Created by rathompson on 9/17/21
 * File: IAMService
 * Project: Cloud Management Module ©Palo Alto Networks
 * © Palo Alto Networks
 */

import axios from 'axios'
import { getVarValue, getFrameworkVar } from '../../common/utils/Sparky'
import { OBJECT } from '../../common/utils/Utils'
import { getTsgToken, getFrameworkServiceUrl } from '@sparky/framework'
import { FF_API_GATEWAY, FF_USE_LOCAL_IAM_SERVICE, FF_INTERNAL_APP } from '../../common/utils/FeatureFlags'

const useAPIGateway = getFrameworkVar(FF_API_GATEWAY, false)
const isInternalApp = getVarValue(FF_INTERNAL_APP, false)
const includeClaims = getVarValue(FF_USE_LOCAL_IAM_SERVICE, false)

const IAM_URL_KEY = 'iam_base_url'
const IAM_SVC_URL_KEY = 'iam_service'
const IAMAPI = axios.create({
  baseURL: getFrameworkServiceUrl(IAM_SVC_URL_KEY) || getVarValue(IAM_URL_KEY),
  timeout: 300 * 1000,
})

IAMAPI.interceptors.request.use(async config => {
  const token = await getTsgToken(config?.tsg_id)
  config.headers = { ...config.headers, 'Authorization': `Bearer ${token}` }
  if (includeClaims) {
    config.headers.claims = token.split('.')[1]
  }
  if (config?.contentType === 'xml') {
    config.headers = { ...config.headers, 'Content-Type': 'text/xml' }
  }
  return config
}, error => {
  return Promise.reject(error)
})

IAMAPI.interceptors.response.use((response) => response, (error) => {
  const e = new Error(error.response?.data?.message || error.response?.data?.msg || error.message)
  e.response = error.response
  throw e
})

const members = {
  get: (tsg_id) => IAMAPI.get(`/tenant_service_groups/${tsg_id}/members`, { tsg_id }),
  delete: (tsg_id, m_id) => IAMAPI.delete(`/tenant_service_groups/${tsg_id}/members/${m_id}`, { tsg_id }),
  post: (tsg_id, {
    identity_type,
    identity_email,
  }) => IAMAPI.post(`/tenant_service_groups/${tsg_id}/members`, OBJECT.reduce({
    identity_type,
    identity_email,
  }), { tsg_id }),
}

const serviceAccounts = useAPIGateway ?
  {
    list: (tsg_id) => IAMAPI.get('/service_accounts', { tsg_id }),
    post: (tsg_id, data) => IAMAPI.post('/service_accounts', OBJECT.reduce(data), { tsg_id }),
    get: (tsg_id, id) => IAMAPI.get(`/service_accounts/${id}`, { tsg_id }),
    put: (tsg_id, id, data) => IAMAPI.put(`/service_accounts/${id}`, OBJECT.reduce(data), { tsg_id }),
    delete: (tsg_id, id) => IAMAPI.delete(`/service_accounts/${id}`, { tsg_id }),
    reset: (tsg_id, id) => IAMAPI.post(`/service_accounts/${id}/operations/reset`, {}, { tsg_id }),
  } :
  {
    list: (tsg_id) => IAMAPI.get(`/tenant_service_groups/${tsg_id}/service_accounts`, { tsg_id }),
    post: (tsg_id, data) => IAMAPI.post(`/tenant_service_groups/${tsg_id}/service_accounts`, OBJECT.reduce(data), { tsg_id }),
    get: (tsg_id, id) => IAMAPI.get(`/tenant_service_groups/${tsg_id}/service_accounts/${id}`, { tsg_id }),
    put: (tsg_id, id, data) => IAMAPI.put(`/tenant_service_groups/${tsg_id}/service_accounts/${id}`, OBJECT.reduce(data), { tsg_id }),
    delete: (tsg_id, id) => IAMAPI.delete(`/tenant_service_groups/${tsg_id}/service_accounts/${id}`, { tsg_id }),
    reset: (tsg_id, id) => IAMAPI.post(`/tenant_service_groups/${tsg_id}/service_accounts/${id}/operations/reset`, {}, { tsg_id }),
  }

const roleType = isInternalApp ? 'internal_roles' : 'roles'
const roles = {
  list: (tsg_id) => IAMAPI.get(`/${roleType}`, { tsg_id }),
  create: (tsg_id, params) => IAMAPI.post(`/${roleType}`, params, { tsg_id }),
  get: (tsg_id, id) => IAMAPI.get(`/${roleType}/${id}`, { tsg_id }),
  update: (tsg_id, id, params) => IAMAPI.put(`/${roleType}/${id}`, params, { tsg_id }),
  delete: (tsg_id, id) => IAMAPI.delete(`/${roleType}/${id}`, { tsg_id }),
}

const permissions = {
  list: (tsg_id) => IAMAPI.get('/permissions', { tsg_id }),
  create: (tsg_id, params) => IAMAPI.post('/permissions', params, { tsg_id }),
  get: (tsg_id, id) => IAMAPI.get(`/permissions/${id}`, { tsg_id }),
  update: (tsg_id, id, params) => IAMAPI.put(`/permissions/${id}`, params, { tsg_id }),
  delete: (tsg_id, id) => IAMAPI.delete(`/permissions/${id}`, { tsg_id }),
}

const policy = isInternalApp ? 'internal_access_policies' : 'access_policies'
const accessPolicies = useAPIGateway ?
  {
    list: (tsg_id) => IAMAPI.get(`/${policy}`, { tsg_id }),
    create: (tsg_id, params) => IAMAPI.post(`/${policy}`, params, { tsg_id }),
    get: (tsg_id, id) => IAMAPI.get(`/${policy}/${id}`, { tsg_id }),
    update: (tsg_id, id, params) => IAMAPI.put(`/${policy}/${id}`, params, { tsg_id }),
    delete: (tsg_id, id) => IAMAPI.delete(`/${policy}/${id}`, { tsg_id }),
  } :
  {
    list: (tsg_id) => IAMAPI.get(`/tenant_service_groups/${tsg_id}/${policy}`, { tsg_id }),
    create: (tsg_id, params) => IAMAPI.post(`tenant_service_groups/${tsg_id}/${policy}`, params, { tsg_id }),
    get: (tsg_id, id) => IAMAPI.get(`/tenant_service_groups/${tsg_id}/${policy}/${id}`, { tsg_id }),
    update: (tsg_id, id, params) => IAMAPI.put(`/tenant_service_groups/${tsg_id}/${policy}/${id}`, params, { tsg_id }),
    delete: (tsg_id, id) => IAMAPI.delete(`/tenant_service_groups/${tsg_id}/${policy}/${id}`, { tsg_id }),
  }

const ping = {
  validate: async (tsg_id, email) => IAMAPI.get(`/sso_users/${email}`, { tsg_id }),
  register: async (tsg_id, { email, firstname, lastname }) => IAMAPI.post('/sso_users', { email, firstname, lastname }, { tsg_id }),
}

const customRoles = {
  list: (tsg_id) => IAMAPI.get('/custom_roles', { tsg_id }),
  create: (tsg_id, params) => IAMAPI.post('/custom_roles', params, { tsg_id }),
  get: (tsg_id, name) => IAMAPI.get(`/custom_roles/${name}`, { tsg_id }),
  update: (tsg_id, name, params) => IAMAPI.put(`/custom_roles/${name}`, params, { tsg_id }),
  delete: (tsg_id, name) => IAMAPI.delete(`/custom_roles/${name}`, { tsg_id })
}

const permissionSets = {
  list: (tsg_id) => IAMAPI.get('/permission_sets', { tsg_id }), // TODO: Enable REAL Request
  create: (tsg_id, params) => IAMAPI.post('/permission_sets', params, { tsg_id }),
  get: (tsg_id, name) => IAMAPI.get(`/permission_sets/${name}`, { tsg_id }),
  update: (tsg_id, name, params) => IAMAPI.put(`/permission_sets/${name}`, params, { tsg_id }),
  delete: (tsg_id, name) => IAMAPI.delete(`/permission_sets/${name}`, { tsg_id })
}

const federations = {
  list: (tsg_id) => IAMAPI.get('/federations', { tsg_id }),
  getMetadata: (tsg_id, domain, metadataUrl, contentType) => IAMAPI.get(`/federations/${domain}/idpMetadata?url=${metadataUrl}`, { tsg_id, contentType }),
  get: (tsg_id, domain) => IAMAPI.get(`/federations/${domain}/spMetadata`, { tsg_id }),
  createOwner: (tsg_id, domain, params) => IAMAPI.post(`/federations/${domain}/owners`, params, { tsg_id }),
  createIDP: (tsg_id, domain, params) => IAMAPI.post(`/federations/${domain}/idps`, params, { tsg_id }),
  // deleteIdp: (tsg_id, domain, idpId) => IAMAPI.delete(`/federations/${domain}/idps/${idpId}`, { tsg_id }),
  delete: (tsg_id, domain) => IAMAPI.delete(`/federations/${domain}`, { tsg_id }),
  deleteOwner: (tsg_id, domain, email) => IAMAPI.delete(`/federations/${domain}/owners/${email}`, { tsg_id }),
  updateIDP: (tsg_id, domain, idpId, params) => IAMAPI.put(`/federations/${domain}/idps/${idpId}`, params, { tsg_id }),
}

const federationMapping = {
  create: (tsg_id, domain) => IAMAPI.put(`/federation_mappings/${domain}`, {}, { tsg_id }),
  delete: (tsg_id, domain) => IAMAPI.delete(`/federation_mappings/${domain}`, { tsg_id }),
  list: (tsg_id) => IAMAPI.get('/federation_mappings', { tsg_id }),
}

const ipRestrictionPolicy = {
  get: (tsg_id) => IAMAPI.get('/ip_restriction_policy', { tsg_id }),
  dryRun: (tsg_id, params) => IAMAPI.put('/ip_restriction_policy?dryRun=true', params, { tsg_id }),
  update: (tsg_id, params) => IAMAPI.put('/ip_restriction_policy', params, { tsg_id }),
  delete: (tsg_id) => IAMAPI.delete('/ip_restriction_policy', { tsg_id })
}

const IAMService = {
  members,
  serviceAccounts,
  roles,
  permissions,
  accessPolicies,
  ping,
  customRoles,
  permissionSets,
  federations,
  federationMapping,
  ipRestrictionPolicy
}

export default IAMService

